import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, PlugZap } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router'

import { consulta, pedir } from '../api/nucleo'
import { Aviso, Boton, Tarjeta } from '../componentes/ui'
import { Editor } from '../motor/Editor'
import { AvisosDeCobro } from './Cobro'

/**
 * La ficha de una pasarela (CLAUDE.md §23): las claves, y **cómo se configura en la
 * pasarela** —dónde se saca cada clave, la dirección de avisos exacta de esta web y qué
 * eventos marcar—, con «Probar la conexión». Con Stripe o Mercado Pago, sin la dirección
 * de avisos ningún pago se confirmaba y nadie se enteraba (auditoría del 29-09-2026). Los
 * pasos y los eventos los da el núcleo, del paquete de cada pasarela: no hay copia aquí.
 */
type Instruccion = {
  pasarela: string
  nombre: string
  pide: string[]
  direccionDeAvisos: string
  panel: null | { nombre: string; url: string }
  pasos: string[]
  eventos: string[]
  avisosObligatorios: boolean
  claves: Record<string, string>
}

type ResultadoDePrueba = { ok: boolean; mensaje: string; avisos: string[] }

/** Los rótulos de los campos de la ficha (los del esquema de `credenciales-de-pago`). */
const CAMPOS_DE_CLAVE: Record<string, string> = { clavePublica: 'Clave pública', secreto: 'Clave secreta', secretoDeWebhook: 'Secreto de los avisos (webhook)' }

const Copiar = ({ texto, etiqueta }: { etiqueta: string; texto: string }) => {
  const [hecho, setHecho] = useState(false)
  return (
    <Boton
      icono={hecho ? <Check /> : <Copy />}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(texto)
          .then(() => {
            setHecho(true)
            window.setTimeout(() => setHecho(false), 2000)
          })
          .catch(() => undefined)
      }}
      pequeno
      tipo="discreto"
    >
      {hecho ? 'Copiado' : etiqueta}
    </Boton>
  )
}

const Instrucciones = ({ instruccion }: { instruccion: Instruccion }) => {
  const pasos = instruccion.pasos.map((p) => p.replace('{avisos}', 'la dirección de avisos de abajo').replace('{eventos}', instruccion.eventos.join(', ')))
  return (
    <Tarjeta>
      <p className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">Cómo se configura {instruccion.nombre}</p>
      {instruccion.panel && (
        <p className="mb-3 text-sm">
          Entra en{' '}
          <a className="font-medium text-violeta underline" href={instruccion.panel.url} rel="noreferrer" target="_blank">
            {instruccion.panel.nombre}
          </a>
          .
        </p>
      )}
      <ol className="mb-4 flex list-decimal flex-col gap-1.5 pl-5 text-sm">
        {pasos.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ol>
      {/* Cada pasarela llama a sus claves a su manera (Izipay: «Usuario» y «Contraseña»): qué va en cada campo de aquí. */}
      {Object.keys(instruccion.claves).length > 0 && (
        <dl className="mb-4 grid gap-1 text-sm">
          <p className="text-[13px] font-medium">Qué va en cada campo</p>
          {Object.entries(instruccion.claves).map(([campo, deDonde]) => (
            <div className="flex flex-wrap gap-x-2" key={campo}>
              <dt className="font-medium">{CAMPOS_DE_CLAVE[campo] ?? campo}:</dt>
              <dd className="text-tinta-2">{deDonde}</dd>
            </div>
          ))}
        </dl>
      )}
      {(instruccion.avisosObligatorios || instruccion.pasos.some((p) => p.includes('{avisos}'))) && (
        <div className="flex flex-col gap-2 rounded-caja border border-linea bg-papel-2/50 p-3">
          <p className="text-[13px] font-medium">Dirección de avisos de esta web{instruccion.avisosObligatorios ? '' : ' (opcional)'}</p>
          <code className="block overflow-x-auto rounded-boton bg-papel px-2.5 py-2 font-mono text-xs break-all">{instruccion.direccionDeAvisos}</code>
          <div className="flex flex-wrap items-center gap-2">
            <Copiar etiqueta="Copiar la dirección" texto={instruccion.direccionDeAvisos} />
          </div>
          {instruccion.eventos.length > 0 && (
            <>
              <p className="mt-1 text-[13px] font-medium">Eventos que hay que marcar</p>
              <ul className="flex flex-wrap gap-1.5">
                {instruccion.eventos.map((e) => (
                  <li className="rounded-pastilla border border-linea bg-papel px-2 py-0.5 font-mono text-xs" key={e}>
                    {e}
                  </li>
                ))}
              </ul>
            </>
          )}
          {instruccion.avisosObligatorios && <p className="text-xs text-tenue">Sin esto, los pagos no se confirman: los pedidos se quedan en «Pendiente de pago» y no sale ningún correo.</p>}
        </div>
      )}
    </Tarjeta>
  )
}

const ProbarConexion = ({ id }: { id: number }) => {
  const [probando, setProbando] = useState(false)
  const [resultado, setResultado] = useState<null | ResultadoDePrueba>(null)
  const [error, setError] = useState<null | string>(null)
  return (
    <div className="flex flex-col gap-2">
      <div>
        <Boton
          cargando={probando}
          disabled={probando}
          icono={<PlugZap />}
          onClick={() => {
            setProbando(true)
            setError(null)
            setResultado(null)
            pedir<ResultadoDePrueba>(`/credenciales-de-pago/probar${consulta({ id })}`, { metodo: 'POST', cuerpo: {} })
              .then(setResultado)
              .catch((e: Error) => setError(e.message))
              .finally(() => setProbando(false))
          }}
          pequeno
          title="Comprueba las claves guardadas sin cobrar nada. Guarda antes lo que hayas cambiado."
        >
          Probar la conexión
        </Boton>
      </div>
      {error && <Aviso tono="error">{error}</Aviso>}
      {resultado && <Aviso tono={resultado.ok ? (resultado.avisos.length ? 'aviso' : 'ok') : 'error'}>{resultado.mensaje}</Aviso>}
      {resultado?.avisos.map((a) => (
        <Aviso key={a} tono="aviso">
          {a}
        </Aviso>
      ))}
    </div>
  )
}

export const FichaDePasarela = ({ espacio, sitio, base }: { base: string; espacio: number; sitio: number }) => {
  const { id } = useParams()
  const nueva = id === 'nuevo'
  const clienteDeConsultas = useQueryClient()
  const { data } = useQuery({
    queryKey: ['pasarelas-instrucciones', sitio],
    queryFn: () => pedir<{ pasarelas: Instruccion[] }>(`/credenciales-de-pago/instrucciones${consulta({ sitio })}`),
    staleTime: 10 * 60_000,
  })
  return (
    <Editor
      // Lo que dicen los avisos de Pedidos (la de prueba, el modo) cambia con lo que se guarde aquí.
      alGuardar={() => void clienteDeConsultas.invalidateQueries({ queryKey: ['cobro', sitio] })}
      base={base}
      cabecera={({ id: doc, valores }) => {
        const elegida = data?.pasarelas.find((p) => p.pasarela === valores.pasarela)
        return (
          <div className="flex flex-col gap-4">
            <AvisosDeCobro espacio={espacio} sitio={sitio} />
            {doc !== null && <ProbarConexion id={doc} />}
            {elegida ? <Instrucciones instruccion={elegida} /> : <p className="text-sm text-tenue">Elige la pasarela y aquí verás cómo configurarla, paso a paso.</p>}
          </div>
        )
      }}
      coleccion="credenciales-de-pago"
      espacio={espacio}
      id={nueva ? null : Number(id)}
      sitio={sitio}
    />
  )
}
